import React from 'react';
import { useFormikContext } from 'formik';

import AdjusterSelectTextFieldFormik from '~/Adjuster/AdjusterSelectTextFieldFormik';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import Text from '~/components/core/TextComponents/Text';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { TextFieldFormik } from '~/components/TextFieldFormik';
import type {
  CreateScheduledReportExportRequestGcsConfig,
  CreateScheduledReportExportRequestS3Config,
  CreateScheduledReportExportRequestSftpConfig,
} from '~/server_shared/openapi/data-contracts';
import { DATA_EXPORT } from '~/Types';

import { SHARED_EXPORT_TEXTS } from './texts';

interface TargetConfigurationValues {
  target_type: string;
  target_configurations: {
    s3_bucket?: CreateScheduledReportExportRequestS3Config;
    gcs_bucket?: CreateScheduledReportExportRequestGcsConfig;
    sftp?: CreateScheduledReportExportRequestSftpConfig;
  };
}

interface TargetConfigurationSectionProps {
  showTitle?: boolean;
}

const TargetConfigurationSection: React.FC<TargetConfigurationSectionProps> = ({ showTitle = true }) => {
  // @ts-expect-error useSysconfig hook needs TypeScript definition
  const { users } = useSysconfig();
  const filteredUsers = users.filter((user: { role: { role_type: string } }) => user.role.role_type !== 'system');
  const { isSubmitting, values } = useFormikContext<TargetConfigurationValues>();
  const { target_type } = values;

  return (
    <div className="-m-8 p-12">
      <div className="p-8">
        {showTitle ? (
          <Text variant={Text.VARIANTS.SM} className="font-bold">
            {SHARED_EXPORT_TEXTS.TARGET_TITLE}
          </Text>
        ) : null}
        <RadioButtonGroupFormik
          id="target_type"
          direction="row"
          showOnly={isSubmitting}
          noSpacing={true}
          allbtnsContainerClassName="flex-nowrap"
          options={[
            {
              text: DATA_EXPORT.TARGET_TYPE.email,
              optionValue: 'email',
            },
            {
              text: DATA_EXPORT.TARGET_TYPE.s3_bucket,
              optionValue: 's3_bucket',
            },
            {
              text: DATA_EXPORT.TARGET_TYPE.sftp,
              optionValue: 'sftp',
            },
            {
              text: DATA_EXPORT.TARGET_TYPE.gcs_bucket,
              optionValue: 'gcs_bucket',
            },
          ]}
        />
      </div>
      <div className="p-8">
        {target_type === 'email' ? (
          <div className="grid grid-cols-2 gap-16">
            {/* @ts-expect-error AdjusterSelectTextFieldFormik is a JS component without TS types */}
            <AdjusterSelectTextFieldFormik
              id="user_id"
              key="user_id"
              label={SHARED_EXPORT_TEXTS.SELECT_SYSTEM_USER}
              className="my-3"
              fullWidth
              disabled={isSubmitting}
              sortAlphabetic
              allUsers={filteredUsers}
            />
          </div>
        ) : null}
        {target_type === 's3_bucket' ? (
          <div className="grid grid-cols-2 gap-16">
            <TextFieldFormik
              id="target_configurations.s3_bucket.path"
              key="target_configurations.s3_bucket.path"
              label={SHARED_EXPORT_TEXTS.S3_PATH}
              fullWidth
              className="my-3"
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id="target_configurations.s3_bucket.bucket_name"
              key="target_configurations.s3_bucket.bucket_name"
              label={SHARED_EXPORT_TEXTS.S3_BUCKET_NAME}
              fullWidth
              className="my-3"
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id="target_configurations.s3_bucket.access_key_id"
              key="target_configurations.s3_bucket.access_key_id"
              label={SHARED_EXPORT_TEXTS.S3_ACCESS_KEY_ID}
              fullWidth
              // @ts-expect-error type is not defined in TextFieldFormik
              type="password"
              className="my-3"
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id="target_configurations.s3_bucket.access_key_secret"
              key="target_configurations.s3_bucket.access_key_secret"
              label={SHARED_EXPORT_TEXTS.S3_ACCESS_KEY_SECRET}
              fullWidth
              // @ts-expect-error type is not defined in TextFieldFormik
              type="password"
              className="my-3"
              disabled={isSubmitting}
            />
          </div>
        ) : null}
        {target_type === 'gcs_bucket' ? (
          <div className="grid grid-cols-2 gap-20">
            <TextFieldFormik
              id="target_configurations.gcs_bucket.path"
              key="target_configurations.gcs_bucket.path"
              label={SHARED_EXPORT_TEXTS.GCS_PATH}
              fullWidth
              className="my-3"
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id="target_configurations.gcs_bucket.bucket_name"
              key="target_configurations.gcs_bucket.bucket_name"
              label={SHARED_EXPORT_TEXTS.GCS_BUCKET_NAME}
              fullWidth
              className="my-3"
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id="target_configurations.gcs_bucket.secret_json"
              key="target_configurations.gcs_bucket.secret_json"
              label={SHARED_EXPORT_TEXTS.GCS_SECRET_JSON}
              fullWidth
              // @ts-expect-error type is not defined in TextFieldFormik
              type="password"
              className="my-3"
              disabled={isSubmitting}
            />
          </div>
        ) : null}
        {target_type === 'sftp' ? (
          <div className="grid grid-cols-2 gap-20">
            <TextFieldFormik
              id="target_configurations.sftp.host"
              key="target_configurations.sftp.host"
              label={SHARED_EXPORT_TEXTS.SFTP_HOST}
              fullWidth
              className="my-3"
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id="target_configurations.sftp.port"
              key="target_configurations.sftp.port"
              label={SHARED_EXPORT_TEXTS.SFTP_PORT}
              fullWidth
              className="my-3"
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id="target_configurations.sftp.path"
              key="target_configurations.sftp.path"
              label={SHARED_EXPORT_TEXTS.SFTP_PATH}
              fullWidth
              className="my-3"
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id="target_configurations.sftp.user"
              key="target_configurations.sftp.user"
              label={SHARED_EXPORT_TEXTS.SFTP_USER}
              fullWidth
              className="my-3"
              disabled={isSubmitting}
            />
            <TextFieldFormik
              id="target_configurations.sftp.password"
              key="target_configurations.sftp.password"
              label={SHARED_EXPORT_TEXTS.SFTP_PASSWORD}
              fullWidth
              // @ts-expect-error type is not defined in TextFieldFormik
              type="password"
              className="my-3"
              disabled={isSubmitting}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TargetConfigurationSection;
